import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Image } from '../../../../modules/design-system';

export interface HeroPackProps {
  imageUrl: string | null;
  title: string;
}

/**
 * Centered pack visual. Pixel-perfect at the four Figma breakpoints, but
 * scales fluidly between them — Figma only specifies the key widths, the
 * pack is expected to grow with viewport.
 *
 * Key targets (anchor values):
 *   375px viewport  → h: 234px  (Figma 31:14257 on 375: pack 187×234)
 *   600px           → h: 300px
 *   900px           → h: 340px
 *   1200px          → h: 380px
 *   1535px          → h: 460px  (Figma 14:3824 on 1535: pack ~260×400)
 *
 * `clamp(min, Nvw, max)` lets each breakpoint scale from its anchor up to
 * the next anchor's min. Height drives width — `object-contain` preserves
 * the pack's intrinsic aspect ratio (~0.8:1). Container queries are read
 * off the nearest `@container` ancestor (PackLayout).
 *
 * Idle animation — replicates the designer's Lottie (Scene-1.json) using
 * motion's keyframe interpolation. The pack floats up, tilts ~1.2° to
 * one side, drops past rest, then mirrors the move to the other side.
 * Loop period = 8.55 s @ Lottie's 60 fps. Pack size in the Lottie was
 * 360 px; the Y offsets are kept in absolute pixels because they happen
 * to read naturally at all four hero sizes (~10 px peak).
 */

const HEIGHT_CLASSES =
  // Mobile (<600px) is bumped ~38% over the original Figma anchor —
  // designer asked for a more dominant pack on small viewports.
  // Desktop breakpoints unchanged.
  'h-[clamp(320px,84vw,440px)] ' +
  '@[600px]:h-[clamp(300px,40vw,380px)] ' +
  '@[900px]:h-[clamp(340px,34vw,400px)] ' +
  '@[1200px]:h-[clamp(380px,30vw,460px)]';

// Designer's Lottie originally held the pack at the peak (duplicated
// keyframes 144 → 180 and 336 → 372). The hold reads as a "stall" in
// motion's tween, so we drop it. Five-keyframe loop — rest → peak-right
// → low intermediate → peak-left → rest — keeps the same pose pattern
// but lets the motion flow through every frame without stopping.
//
// Extras layered on top of the Lottie's y + rotate, for energy:
//   • larger amplitude (~14 px peak vs. the original ~10.75 px)
//   • a subtle scale pulse (1 → 1.025) that swells with each lift
//   • a small horizontal drift that breaks the "vertical bobbing only"
//     symmetry and gives the float an actual circular trajectory
const FLOAT_KEYS = {
  y: [0, -14, -2, -14, 0],
  rotate: [0, 2.4, 0, -2.4, 0],
  x: [0, 5, 0, -5, 0],
  scale: [1, 1.025, 1, 1.025, 1],
} as const;

const FLOAT_TIMES = [0, 0.26, 0.5, 0.74, 1];

const FLOAT_DURATION = 6.4;

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

export function HeroPack({ imageUrl, title }: HeroPackProps) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      className={
        'inline-block aspect-[4/5] rounded-[28px] ' +
        '[filter:drop-shadow(0_14px_64px_rgba(0,0,0,0.45))] ' +
        HEIGHT_CLASSES
      }
      {...(reduced
        ? {}
        : {
            animate: {
              y: [...FLOAT_KEYS.y],
              x: [...FLOAT_KEYS.x],
              rotate: [...FLOAT_KEYS.rotate],
              scale: [...FLOAT_KEYS.scale],
            },
            transition: {
              duration: FLOAT_DURATION,
              times: FLOAT_TIMES,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'loop' as const,
            },
          })}
      style={{ willChange: 'transform' }}
    >
      <Image
        src={imageUrl}
        alt={`${title} pack`}
        className="block h-full w-full"
        imgClassName="absolute inset-0 h-full w-full object-contain"
      />
    </motion.div>
  );
}
